/**
 * PropertiesPanel — the "Widget Config" panel on the right of the
 * builder. Renders inputs for title, type, KPI value/trend, cross-filter
 * opt-ins, click behaviour, compare-with, and small-multiples trellis-by.
 *
 * Extracted from DashboardBuilderPage.js (iter-150). Presentation-only:
 * the parent owns `widgets` state and hands us `selected` + `setWidgets`.
 */
import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { WIDGET_TYPES } from './widgetShared';
import { emitCoachEvent } from '@/coach/coachApi';

// Certified metrics / dimensions (product metadata) offered as chart data.
const CERTIFIED_MEASURES = [
  ['written_premium', 'Written Premium'], ['earned_premium', 'Earned Premium'],
  ['loss_ratio', 'Loss Ratio'], ['claim_count', 'Claim Count'],
];
const CERTIFIED_DIMENSIONS = [
  ['state', 'State'], ['line_of_business', 'Line of Business'],
  ['policy_year', 'Policy Year'], ['agency', 'Agency'],
];
const CHART_WIDGET_TYPES = new Set(['bar_chart', 'line_chart', 'area_chart', 'pie_chart']);

export function PropertiesPanel({ selected, widgets, setWidgets }) {
  if (!selected) return null;
  const update = (patch) => setWidgets(prev => prev.map(
    w => w.widget_id === selected.widget_id ? { ...w, ...patch } : w));
  const updateConfig = (patch) => setWidgets(prev => prev.map(
    w => w.widget_id === selected.widget_id ? { ...w, config: { ...w.config, ...patch } } : w));

  return (
    <div className="p-3 flex-1 overflow-auto">
      <Label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2 block">Widget Config</Label>
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-muted-foreground">Title</Label>
          <Input
            value={selected.title}
            onChange={e => update({ title: e.target.value })}
            className="rounded-lg bg-muted/50 border-border/50 text-xs h-8 mt-1"
            data-testid="widget-title-input"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Type</Label>
          <Select value={selected.widget_type} onValueChange={val => update({ widget_type: val })}>
            <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 text-xs h-8 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {WIDGET_TYPES.map(wt => (
                <SelectItem key={wt.type} value={wt.type} className="text-xs">{wt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {selected.widget_type === 'kpi' && (
          <>
            <div>
              <Label className="text-xs text-muted-foreground">Value</Label>
              <Input value={selected.config?.value || ''}
                onChange={e => updateConfig({ value: e.target.value })}
                className="rounded-lg bg-muted/50 border-border/50 text-xs h-8 mt-1" placeholder="e.g. $4.2B" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Trend</Label>
              <Input value={selected.config?.trend || ''}
                onChange={e => updateConfig({ trend: e.target.value })}
                className="rounded-lg bg-muted/50 border-border/50 text-xs h-8 mt-1" placeholder="e.g. +6.8%" />
            </div>
          </>
        )}

        {CHART_WIDGET_TYPES.has(selected.widget_type) && (
          <div className="border-t border-border/40 pt-3 mt-2 space-y-2">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Chart Data</Label>
            <div>
              <Label className="text-xs text-muted-foreground">Measure</Label>
              <Select value={selected.config?.measure || ''}
                onValueChange={val => { updateConfig({ measure: val }); emitCoachEvent('dashboard_measure_set', 'dashboard-widget-measure'); }}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 text-xs h-8 mt-1" data-testid="widget-measure-select" data-coach-target="dashboard-widget-measure">
                  <SelectValue placeholder="Choose a measure" />
                </SelectTrigger>
                <SelectContent>
                  {CERTIFIED_MEASURES.map(([val, label]) => (
                    <SelectItem key={val} value={val} className="text-xs">{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Dimension</Label>
              <Select value={selected.config?.dimension || ''}
                onValueChange={val => { updateConfig({ dimension: val }); emitCoachEvent('dashboard_dimension_set', 'dashboard-widget-dimension'); }}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 text-xs h-8 mt-1" data-testid="widget-dimension-select" data-coach-target="dashboard-widget-dimension">
                  <SelectValue placeholder="Choose a dimension" />
                </SelectTrigger>
                <SelectContent>
                  {CERTIFIED_DIMENSIONS.map(([val, label]) => (
                    <SelectItem key={val} value={val} className="text-xs">{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <div className="border-t border-border/40 pt-3 mt-2">
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Cross-filter</Label>
          <div className="flex items-center gap-2 mt-1.5">
            <input type="checkbox" id="xf-src" checked={!!selected.config?.cross_filter_source}
              onChange={e => updateConfig({ cross_filter_source: e.target.checked })}
              data-testid="widget-cross-filter-source" />
            <label htmlFor="xf-src" className="text-xs text-foreground cursor-pointer">Clicks publish filter</label>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <input type="checkbox" id="xf-rcv" checked={!!selected.config?.cross_filter_receive}
              onChange={e => updateConfig({ cross_filter_receive: e.target.checked })}
              data-testid="widget-cross-filter-receive" />
            <label htmlFor="xf-rcv" className="text-xs text-foreground cursor-pointer">Apply filters from others</label>
          </div>
        </div>

        <div className="border-t border-border/40 pt-3 mt-2">
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Click Behaviour</Label>
          <Select value={selected.config?.click_action?.type || 'none'}
            onValueChange={val => updateConfig({
              click_action: val === 'none' ? null : { ...(selected.config?.click_action || {}), type: val }
            })}>
            <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 text-xs h-8 mt-1" data-testid="widget-click-action-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none" className="text-xs">None</SelectItem>
              <SelectItem value="drill_dashboard" className="text-xs">Drill to dashboard</SelectItem>
              <SelectItem value="open_url" className="text-xs">Open URL</SelectItem>
              <SelectItem value="ai_explore" className="text-xs">Send to AI Explore</SelectItem>
            </SelectContent>
          </Select>
          {selected.config?.click_action?.type && (
            <Input value={selected.config?.click_action?.target || ''}
              onChange={e => updateConfig({ click_action: { ...selected.config.click_action, target: e.target.value } })}
              placeholder={
                selected.config.click_action.type === 'drill_dashboard' ? 'dashboard_id or slug' :
                selected.config.click_action.type === 'open_url' ? 'https://…' :
                "Prompt seed (e.g. 'why is $CLICKED_LABEL trending down?')"
              }
              className="rounded-lg bg-muted/50 border-border/50 text-xs h-8 mt-2" data-testid="widget-click-action-target" />
          )}
        </div>

        <div className="border-t border-border/40 pt-3 mt-2">
          <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Compare With</Label>
          <Select value={selected.config?.compare_with || 'none'}
            onValueChange={val => updateConfig({ compare_with: val === 'none' ? null : val })}>
            <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 text-xs h-8 mt-1" data-testid="widget-compare-with">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none" className="text-xs">Off</SelectItem>
              {widgets.filter(w => w.widget_id !== selected.widget_id).map(w => (
                <SelectItem key={w.widget_id} value={w.widget_id} className="text-xs">{w.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selected.widget_type === 'small_multiples' && (
          <div className="border-t border-border/40 pt-3 mt-2">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Trellis by</Label>
            <Input value={selected.config?.trellis_by || ''}
              onChange={e => updateConfig({ trellis_by: e.target.value })}
              placeholder="dimension field name (e.g. region)"
              className="rounded-lg bg-muted/50 border-border/50 text-xs h-8 mt-1" data-testid="widget-trellis-by" />
          </div>
        )}
      </div>
    </div>
  );
}

export default PropertiesPanel;
